import type { OrgTreeNode } from '../../hooks/useOrganizationalStructure';

// =============================================================================
// Czyste reguły układu diagramu struktury — bez Reacta, żeby dało się je
// zmierzyć wprost. Powód istnienia: docs/07-DESIGN/DIAGRAM_STRUKTURY_ORGANIZACYJNEJ.md
// §1 — dziesięć stanowisk w jednym poziomym wierszu nie mieściło się na 1920 px.
// =============================================================================

/** Powyżej tylu dzieci węzeł przechodzi w kolumnę nawet, gdy dzieci mają poddrzewa. */
export const PROG_KOLUMNY = 4;

/** Węzły z dziećmi na tej głębokości i głębiej startują zwinięte. */
export const GLEBOKOSC_ROZWINIETA = 3;

export const SKALA_MIN = 0.4;
export const SKALA_MAX = 1.5;
export const KROK_SKALI = 1.2;

export type Uklad = 'wiersz' | 'kolumna';

/**
 * Kolumna ⇔ węzeł ma dzieci ∧ (wszystkie dzieci są liśćmi ∨ dzieci > PROG_KOLUMNY).
 *
 * Pierwszy człon to reguła „stanowiska pod działem idą w pion” — liście nie
 * potrzebują szerokości na własne poddrzewa. Drugi chroni przed dwunastoma
 * działami w wierszu: szerokość diagramu ma zależeć od głębokości, nie od
 * liczby elementów na poziomie.
 */
export function czyUkladKolumnowy(node: Pick<OrgTreeNode, 'children'>): boolean {
  if (node.children.length === 0) return false;
  if (node.children.length > PROG_KOLUMNY) return true;
  return node.children.every((dziecko) => dziecko.children.length === 0);
}

export function ukladDzieci(node: Pick<OrgTreeNode, 'children'>): Uklad {
  return czyUkladKolumnowy(node) ? 'kolumna' : 'wiersz';
}

/**
 * Skala, przy której treść o naturalnej szerokości `szerokoscTresci` mieści
 * się w oknie `szerokoscOkna`. Nigdy powyżej 1 — „dopasuj” zmniejsza, nie
 * rozdmuchuje trzech kart na cały ekran. Dół zakresu to SKALA_MIN; poniżej
 * karty stają się nieczytelne i lepiej pozwolić przewijać.
 */
export function skalaDopasowania(szerokoscTresci: number, szerokoscOkna: number): number {
  if (szerokoscTresci <= 0 || szerokoscOkna <= 0) return 1;
  const surowa = szerokoscOkna / szerokoscTresci;
  return ograniczSkale(Math.min(1, surowa));
}

export function ograniczSkale(skala: number): number {
  if (!Number.isFinite(skala)) return 1;
  return Math.min(SKALA_MAX, Math.max(SKALA_MIN, skala));
}

/** Identyfikatory węzłów z dziećmi leżących na głębokości ≥ `odGlebokosci`. */
export function domyslnieZwiniete(
  tree: OrgTreeNode[],
  odGlebokosci: number = GLEBOKOSC_ROZWINIETA
): Set<string> {
  const wynik = new Set<string>();
  const odwiedz = (node: OrgTreeNode, depth: number) => {
    if (node.children.length > 0 && depth >= odGlebokosci) wynik.add(node.id);
    node.children.forEach((dziecko) => odwiedz(dziecko, depth + 1));
  };
  tree.forEach((node) => odwiedz(node, 0));
  return wynik;
}

/** Wszystkie węzły mające dzieci — cel „Zwiń wszystko”. */
export function wszystkieZDziecmi(tree: OrgTreeNode[]): Set<string> {
  return domyslnieZwiniete(tree, 0);
}

export interface LicznikTypow {
  department: number;
  position: number;
  team: number;
}

export function policzTypy(tree: OrgTreeNode[]): LicznikTypow {
  const wynik: LicznikTypow = { department: 0, position: 0, team: 0 };
  const odwiedz = (node: OrgTreeNode) => {
    wynik[node.unit_type] += 1;
    node.children.forEach(odwiedz);
  };
  tree.forEach(odwiedz);
  return wynik;
}
